/**
 * Auto-validate + save the response parser whenever its code changes.
 *
 * After the Model Configuration "Next" gate was removed, parser changes must
 * persist without an explicit Validate click. ``POST /validate-code`` already
 * saves the parser when valid; this hook triggers that endpoint (debounced)
 * and lets the page sync local / global usecase state via callbacks.
 *
 * Behaviour mirrors ``useTeacherPromptAutoSave``:
 *   - Debounced ``PARSER_AUTO_VALIDATE_DEBOUNCE_MS`` after the last edit.
 *   - Skips placeholder / empty bodies, Viewer role, and already-saved code.
 *   - In-flight requests are superseded by newer edits (generation counter).
 *   - Unrelated parent re-renders that return a new ``onValidated`` lambda
 *     do not restart the debounce window (latest-ref pattern).
 */

import { useEffect, useRef, useState } from 'react';
import { data_from_server_post } from '@/shared/http/client';

export const PARSER_AUTO_VALIDATE_DEBOUNCE_MS = 650;

const PLACEHOLDER_BODY = '# Implement your logic here to return the expected output';

const normalizeCode = (code) => (code || '').replace(/\s+/g, '').replace(/["']/g, '"');

/**
 * @param {Object} args
 * @param {boolean} [args.enabled=true] Gate (comparison mode off → false).
 * @param {string} [args.role] Auth role; "Viewer" disables validate/save.
 * @param {string} [args.clientId]
 * @param {string} [args.llmUsecaseId]
 * @param {string} [args.taskType]
 * @param {string} [args.userCode] Parser body only (no ``def`` signature).
 * @param {string} [args.fullCode] Full Python function (signature + body).
 * @param {string} [args.rawResponse] Sample raw teacher output to validate against.
 * @param {string|null} [args.savedParserFunction] Last known saved parser on the usecase.
 * @param {(result: Object, fullCode: string) => void} [args.onValidated]
 * @param {(error: Object|string, fullCode: string) => void} [args.onValidationFailed]
 *
 * @returns {{ validating: boolean, lastResult: Object|null }}
 */
export const useParserFunctionAutoValidate = ({
    enabled = true,
    role,
    clientId,
    llmUsecaseId,
    taskType,
    userCode,
    fullCode,
    rawResponse,
    savedParserFunction = null,
    onValidated,
    onValidationFailed,
}) => {
    const [validating, setValidating] = useState(false);
    const [lastResult, setLastResult] = useState(null);

    const lastAttemptKeyRef = useRef(null);
    const generationRef = useRef(0);
    const timerRef = useRef(null);
    const mountedRef = useRef(true);

    useEffect(() => {
        return () => {
            mountedRef.current = false;
        };
    }, []);

    // Reset attempt tracking when the active usecase changes so a freshly
    // loaded parser is not treated as "already attempted" from a prior task.
    useEffect(() => {
        lastAttemptKeyRef.current = null;
        setLastResult(null);
        setValidating(false);
        if (timerRef.current != null) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    }, [llmUsecaseId]);

    const latestRef = useRef({
        onValidated,
        onValidationFailed,
    });
    latestRef.current = {
        onValidated,
        onValidationFailed,
    };

    const isViewer = role === 'Viewer';
    const bodyTrimmed = (userCode || '').trim();
    const isPlaceholder =
        !bodyTrimmed ||
        normalizeCode(bodyTrimmed) === normalizeCode(PLACEHOLDER_BODY);

    const attemptKey = `${fullCode || ''}::${rawResponse || ''}`;
    const matchesSaved =
        savedParserFunction &&
        normalizeCode(fullCode) === normalizeCode(savedParserFunction);

    useEffect(() => {
        if (!enabled || isViewer) return;
        if (!clientId || !llmUsecaseId || !taskType) return;
        if (!fullCode || !rawResponse) return;
        if (isPlaceholder) return;

        // Already persisted for this code — mark as attempted so we don't
        // re-hit the endpoint on every remount / hydration.
        if (matchesSaved) {
            lastAttemptKeyRef.current = attemptKey;
            return;
        }

        if (attemptKey === lastAttemptKeyRef.current) return;

        if (timerRef.current != null) {
            clearTimeout(timerRef.current);
        }

        let cancelled = false;
        const generation = ++generationRef.current;

        timerRef.current = setTimeout(() => {
            lastAttemptKeyRef.current = attemptKey;
            if (!mountedRef.current || cancelled) return;

            setValidating(true);

            const body = {
                code: fullCode,
                raw_response: rawResponse,
                task_type: taskType,
                client_id: clientId,
                llm_usecase_id: llmUsecaseId,
            };

            data_from_server_post(
                'validate-code',
                body,
                (result) => {
                    if (!mountedRef.current || cancelled || generation !== generationRef.current) {
                        return;
                    }
                    setValidating(false);
                    setLastResult(result);

                    if (result?.detail) {
                        latestRef.current.onValidationFailed?.(result.detail, fullCode);
                        return;
                    }

                    if (result?.is_valid) {
                        latestRef.current.onValidated?.(result, fullCode);
                    } else {
                        latestRef.current.onValidationFailed?.(result, fullCode);
                    }
                },
                (error_message) => {
                    if (!mountedRef.current || cancelled || generation !== generationRef.current) {
                        return;
                    }
                    setValidating(false);
                    const failed = { success: false, message: error_message };
                    setLastResult(failed);
                    latestRef.current.onValidationFailed?.(failed, fullCode);
                }
            );
        }, PARSER_AUTO_VALIDATE_DEBOUNCE_MS);

        return () => {
            cancelled = true;
            if (timerRef.current != null) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
        };
    }, [
        enabled,
        isViewer,
        clientId,
        llmUsecaseId,
        taskType,
        fullCode,
        rawResponse,
        isPlaceholder,
        matchesSaved,
        attemptKey,
    ]);

    return { validating, lastResult };
};
